import { Address } from "components/Address/Address";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { ExchangeRateSummary } from "components/design/ExchangeRateSummary";
import { InvoiceItemGroup } from "components/design/InvoiceItemGroup";
import { InfoItem } from "components/DesignSystem/InfoItem/InfoItem";
import { LineItemInfoTable } from "components/LineItems/LineItemInfoTable";
import { DISPLAY_DATE } from "constants/date";
import { USD } from "constants/currency";
import dayjs from "dayjs";
import { useGetExchangeRateQuery } from "store/apis/invoices";
import { VendorBill } from "types/Models/vendorBills";

export const BillInfo = ({ bill }: { bill: VendorBill }) => {
  const { data: exchangeRate } = useGetExchangeRateQuery(
    {
      base_currency: USD,
      target_currency: bill?.currency!,
      date: bill?.bill_date!,
    },
    { skip: !bill || !bill?.bill_date || bill?.currency === USD }
  );

  const usdEquivalent = exchangeRate?.exchange_rate
    ? Number(bill?.due_balance) / Number(exchangeRate.exchange_rate)
    : null;

  if (!bill) {
    return null;
  }

  return (
    <div className="t-pb-10 t-gap-16 t-flex t-flex-col">
      <div className="t-flex t-flex-col t-gap-12">
        <InvoiceItemGroup title="Vendor Details" required>
          <div className="t-grid t-grid-cols-2 t-gap-y-6 t-gap-x-20">
            <InfoItem label="Name">
              {bill?.entity_merchant_data?.name || "-"}
            </InfoItem>
            <InfoItem label="Email">
              {bill?.entity_merchant_data?.email || "-"}
            </InfoItem>
            {bill?.merchant_address && (
              <InfoItem label="Address">
                <Address address={bill?.merchant_address} />
              </InfoItem>
            )}
          </div>
        </InvoiceItemGroup>

        <InvoiceItemGroup title="Bill Details">
          <div className="t-grid t-grid-cols-2 t-gap-y-6 t-gap-x-20">
            <InfoItem label="Title">{bill?.title || "-"}</InfoItem>
            <InfoItem label="Bill Number">{bill?.bill_number || "-"}</InfoItem>
            <InfoItem label="Bill Date">
              {bill?.bill_date
                ? dayjs(bill?.bill_date).format(DISPLAY_DATE)
                : "-"}
            </InfoItem>
            <InfoItem label="Due Date">
              {bill?.due_date
                ? dayjs(bill?.due_date).format(DISPLAY_DATE)
                : "-"}
            </InfoItem>
            <InfoItem label="Preferred Currency">
              {bill?.currency || "-"}
            </InfoItem>
          </div>
        </InvoiceItemGroup>
      </div>

      <InvoiceItemGroup title="Item Details">
        <LineItemInfoTable invoice={bill} />
      </InvoiceItemGroup>

      <div className="t-flex t-flex-col t-gap-12">
        <InvoiceItemGroup title="Tax & Discount">
          <div className="t-grid t-grid-cols-3 t-gap-y-6 t-gap-x-20">
            <InfoItem label="Tax">
              {bill?.tax_type === "PERCENT" ? (
                `${bill?.tax || 0}%`
              ) : (
                <AmountSuperScript
                  amount={Number(bill.tax)}
                  currencyCode={bill.currency}
                />
              )}
            </InfoItem>
            <InfoItem label="Discount">
              {bill?.discount_type === "PERCENT" ? (
                `${bill?.discount || 0}%`
              ) : (
                <AmountSuperScript
                  amount={Number(bill.discount)}
                  currencyCode={bill.currency}
                />
              )}
            </InfoItem>
            <InfoItem label="Advance Paid">
              <span className="t-flex t-items-center t-gap-1">
                <AmountSuperScript
                  amount={Number(bill?.paid_amount) || 0}
                  currencyCode={bill.currency}
                />
              </span>
            </InfoItem>
          </div>
        </InvoiceItemGroup>

        {bill?.currency && bill.currency !== USD && (
          <ExchangeRateSummary
            rate={exchangeRate?.exchange_rate}
            currency={bill.currency}
            date={bill.bill_date}
            totalAmount={Number(bill.due_balance)}
            usdEquivalent={usdEquivalent}
          />
        )}

        <InvoiceItemGroup title="Payment Details">
          <InfoItem label="Payment Instructions">
            <span className="t-whitespace-pre-wrap">
              {bill?.payment_instructions || "-"}
            </span>
          </InfoItem>
          <InfoItem label="Payment Link">
            {bill.payment_link ? bill.payment_link : "-"}
          </InfoItem>
          <InfoItem label="Payment Terms">
            <span className="t-whitespace-pre-wrap">{bill?.terms || "-"}</span>
          </InfoItem>
        </InvoiceItemGroup>
      </div>
    </div>
  );
};
